import React, { useId, useMemo } from 'react';
import { components as ReactSelectComponents } from 'react-select';
import { formFieldSelectStyles } from './styles';

/**
 * The label / error / aria wrapper shared by AppSelect and AppAsyncSelect.
 *
 * Extracted rather than duplicated because the aria-describedby composition below is
 * subtle and easy to get wrong: react-select manages that attribute itself, pointing it
 * at its own placeholder and live-region nodes, and OVERWRITES whatever a caller passes
 * as a prop. An error message linked the naive way is simply never announced. Keeping
 * one implementation means the sync and async selects cannot drift on it.
 */

/**
 * Caps how many options the menu PAINTS, after react-select has filtered them.
 *
 * Restores behaviour that came free with Tom Select and was lost in the migration: its
 * `maxOptions` defaulted to 50 (tom-select/src/defaults.ts) and neither old wrapper
 * overrode it, so every select in the app has always rendered at most 50 rows. react-select
 * has no equivalent and paints every option, which on the client list means thousands of
 * DOM nodes in one scroller.
 *
 * Capping here rather than by slicing the `options` prop is deliberate: MenuList receives
 * the already-FILTERED children, so typing still searches the whole list and then shows the
 * best N of the matches — which is what Tom Select did. Slicing the input list instead would
 * make search blind to anything past the cap.
 *
 * Unlike Tom Select, the truncation is stated rather than silent: cutting a list without
 * saying so is how "my client is missing from the dropdown" happens.
 */
const makeMenuList = (maxOptions) => (props) => {
  const children = React.Children.toArray(props.children);
  // An empty menu renders a single no-options/loading message, not options — never cap it.
  const isOptionList = children.length > 1 || (children[0]?.props?.data !== undefined);
  if (!maxOptions || !isOptionList || children.length <= maxOptions) {
    return <ReactSelectComponents.MenuList {...props} />;
  }

  return (
    <ReactSelectComponents.MenuList {...props}>
      {children.slice(0, maxOptions)}
      {/* aria-hidden because MenuList is the listbox, and a non-option child of a listbox
          is invalid ARIA. Screen readers get the same information through
          `screenReaderStatus` below, which is the channel react-select already uses to
          announce result counts — so it is spoken once, not twice. */}
      <div className="app-select__menu-truncation" aria-hidden="true">
        <svg
          className="app-select__menu-truncation-icon"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span>
          Showing
          {' '}
          <strong>{maxOptions}</strong>
          {' of '}
          <strong>{children.length}</strong>
          {' — keep typing to narrow'}
        </span>
      </div>
    </ReactSelectComponents.MenuList>
  );
};

/**
 * What a screen reader hears when the list is capped.
 *
 * react-select announces "N results available" through its own live region; this replaces
 * that string when the menu is truncated, so the cap is not something only sighted users
 * can see. Mirrors the visible footer rather than adding a second live region.
 */
const makeScreenReaderStatus = (maxOptions) => ({ count }) => (
  maxOptions && count > maxOptions
    ? `Showing ${maxOptions} of ${count} results. Keep typing to narrow the list.`
    : `${count} result${count === 1 ? '' : 's'} available.`
);

/**
 * Ids, the aria-composing Input override, the option cap, and error styling for a select
 * field.
 *
 * The overrides are memoized on the values they close over: react-select remounts these
 * components when their identity changes, which would drop focus mid-typing.
 */
export const useSelectFieldChrome = ({ id, error, ariaDescribedBy, required, maxOptions }) => {
  const generatedId = useId();
  const inputId = id || `${generatedId}-input`;
  const errorId = `${generatedId}-error`;

  const describedBy = [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(' ');

  const selectComponents = useMemo(() => ({
    MenuList: makeMenuList(maxOptions),
    Input: (inputProps) => (
      <ReactSelectComponents.Input
        {...inputProps}
        aria-describedby={
          [inputProps['aria-describedby'], describedBy].filter(Boolean).join(' ') || undefined
        }
        aria-required={required || undefined}
        // Preserve a caller-supplied aria-invalid when there is no `error` of our own:
        // spreading inputProps first and then writing `undefined` would silently erase it.
        aria-invalid={error ? true : inputProps['aria-invalid']}
      />
    ),
  }), [describedBy, required, error, maxOptions]);

  const styles = useMemo(() => (
    error
      ? {
        ...formFieldSelectStyles,
        control: (base, state) => ({
          ...formFieldSelectStyles.control(base, state),
          borderColor: '#dc3545',
          boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(220, 53, 69, 0.15)' : 'none',
          ':hover': { borderColor: '#dc3545' },
        }),
      }
      : formFieldSelectStyles
  ), [error]);

  const screenReaderStatus = useMemo(() => makeScreenReaderStatus(maxOptions), [maxOptions]);

  return { inputId, errorId, selectComponents, styles, screenReaderStatus };
};

/**
 * The markup around the control: the mb-3 wrapper, the label with its required marker,
 * and the invalid-feedback message. Matches what the TomSelect wrappers rendered, so a
 * migrated call site keeps its spacing and its error display.
 *
 * The label carries htmlFor, which TomSelect's did not — clicking its label focused
 * nothing.
 */
export const SelectFieldShell = ({
  label,
  required = false,
  inputId,
  error,
  errorId,
  className = '',
  children,
}) => (
  <div className={`mb-3 ${className}`.trim()}>
    {label && (
      <label className="form-label" htmlFor={inputId}>
        {label} {required && <span className="text-danger">*</span>}
      </label>
    )}
    {children}
    {error && <div className="invalid-feedback" id={errorId}>{error}</div>}
  </div>
);
